import { apiFetch } from './apiClient';
import { RecordingOutbox } from './recordingOutbox';
import RecordingTransport from './recordingTransport';

const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
const DEFAULT_MAX_CHUNKS = 4096;
const MAX_CAPTURE_MS = 8 * 60 * 60 * 1000;
// Reserve room for the current independent chunk and envelope metadata.
const MAX_CAPTURE_BYTES = 84 * 1024 * 1024;
const sessionLockName = id => `noteician-recording:${id}`;
const errorFor = key => new Error(`recording.${key}`);

// Capture, upload, and note generation have separate lifetimes. Captured audio
// stays on disk until Stop is acknowledged; network failure never means stop.
export default class ChunkedVoiceRecorder {
  constructor(options = {}) {
    this.options = options;
    this.outbox = options.outbox || new RecordingOutbox();
    this.state = 'idle';
    this.sessionId = null;
    this.stream = null;
    this.mediaRecorder = null;
    this.chunkIndex = 0;
    this.maxChunkCount = DEFAULT_MAX_CHUNKS;
    this.maxChunkDuration = 30000;
    this.maxChunkSize = 5 * 1024 * 1024;
    this.totalElapsedTime = 0;
    this.totalRecordedBytes = 0;
    this.captureStartedAt = null;
    this.unsavedChunks = new Map();
    this.nextChunkToSend = 0;
    this.transport = null;
    this.command = Promise.resolve();
    this.streamTask = null;
    this.persistTask = null;
    this.stopData = null;
    this.disposed = false;
    this.trackListeners = [];
  }

  get canResume() {
    return Boolean(this.sessionId && !this.stopData &&
      ['paused', 'error'].includes(this.state) && this.chunkIndex < this.maxChunkCount &&
      this.totalElapsedTime < MAX_CAPTURE_MS && this.totalRecordedBytes < MAX_CAPTURE_BYTES);
  }

  setState(state) {
    if (this.disposed) return;
    this.state = state;
    this.options.onStateChange?.(state);
  }

  notifyError(error) {
    if (!this.disposed) this.options.onError?.(error);
  }

  serialize(action) {
    const next = this.command.then(() => {
      if (this.disposed) throw errorFor('sessionError');
      return action();
    });
    this.command = next.catch(() => {});
    return next;
  }

  elapsed() {
    return this.totalElapsedTime + (this.captureStartedAt === null ? 0 : Date.now() - this.captureStartedAt);
  }

  startTimer() {
    if (this.captureStartedAt !== null) return;
    this.captureStartedAt = Date.now();
    this.timer = setInterval(() => {
      this.options.onTick?.(this.elapsed());
      if (this.elapsed() >= MAX_CAPTURE_MS) {
        this.captureFailed(errorFor('sizeLimit'));
        this.totalElapsedTime = Math.min(this.totalElapsedTime, MAX_CAPTURE_MS);
      }
    }, 1000);
  }

  stopTimer() {
    this.totalElapsedTime = this.elapsed();
    this.captureStartedAt = null;
    clearInterval(this.timer);
    this.timer = null;
    this.options.onTick?.(this.totalElapsedTime);
  }

  isStreamRecordable() {
    return this.stream?.getAudioTracks().some(track =>
      track.readyState === 'live' && track.enabled !== false && !track.muted) || false;
  }

  releaseStream() {
    this.trackListeners.forEach(([track, event, callback]) => track.removeEventListener(event, callback));
    this.trackListeners = [];
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = null;
    if (!this.disposed) this.options.onStreamReady?.(null);
  }

  async acquireStream() {
    if (this.isStreamRecordable()) return;
    this.releaseStream();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (_) {
      throw errorFor('microphoneError');
    }
    if (this.disposed) {
      stream.getTracks().forEach(track => track.stop());
      throw errorFor('sessionError');
    }
    this.stream = stream;
    if (!this.isStreamRecordable()) {
      this.releaseStream();
      throw errorFor('microphoneError');
    }
    for (const track of stream.getAudioTracks()) {
      for (const event of ['ended', 'mute']) {
        const callback = () => this.captureFailed(errorFor('microphoneLost'));
        track.addEventListener(event, callback);
        this.trackListeners.push([track, event, callback]);
      }
    }
    this.options.onStreamReady?.(stream);
  }

  async request(path, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await apiFetch(path, { ...options, signal: controller.signal });
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  async sessionStatus(sessionId) {
    return this.request(`/api/recording/status/${encodeURIComponent(sessionId)}`);
  }

  async lockSession(sessionId) {
    if (!navigator.locks) return;
    await new Promise((resolve, reject) => {
      navigator.locks.request(sessionLockName(sessionId), { ifAvailable: true }, async lock => {
        if (!lock || this.disposed) { reject(errorFor('sessionError')); return; }
        await new Promise(release => {
          this.releaseSessionLock = release;
          resolve();
        });
      }).catch(reject);
    });
  }

  checkpoint() {
    return {
      sessionId: this.sessionId,
      createdAt: this.createdAt,
      nextChunkIndex: this.chunkIndex,
      actualDurationSeconds: Math.ceil(this.elapsed() / 1000),
    };
  }

  start() {
    return this.serialize(async () => {
      if (this.sessionId) return;
      this.setState('starting');
      try {
        await this.outbox.pruneExpired();
        await this.acquireStream();
        const session = await this.request('/api/recording/start', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
        });
        if (!session.sessionId) throw errorFor('sessionError');
        this.sessionId = session.sessionId;
        this.createdAt = Date.now();
        await this.lockSession(this.sessionId);
        this.maxChunkDuration = Math.min(30000, session.maxChunkDurationMs || 30000);
        this.maxChunkSize = session.maxChunkSize || this.maxChunkSize;
        this.maxChunkCount = session.maxChunkCount || DEFAULT_MAX_CHUNKS;
        await this.outbox.saveSession(this.checkpoint());
        this.transport = this.createTransport();
        await this.transport.connect();
        await this.startChunk();
      } catch (cause) {
        this.releaseStream();
        const error = this.publicError(cause, this.sessionId ? 'storageError' : 'startError');
        this.setState('error');
        this.notifyError(error);
        throw error;
      }
    });
  }

  createTransport() {
    const options = {
      sessionId: this.sessionId,
      request: (...args) => this.request(...args),
      onFallback: () => this.notifyError(errorFor('fallbackNotice')),
    };
    return this.options.transportFactory ? this.options.transportFactory(options) : new RecordingTransport(options);
  }

  publicError(cause, fallback) {
    return cause?.message?.startsWith('recording.') ? cause : errorFor(fallback);
  }

  async startChunk() {
    if (!this.isStreamRecordable()) throw errorFor('microphoneLost');
    if (this.chunkIndex >= this.maxChunkCount) throw errorFor('sessionError');
    if (this.elapsed() >= MAX_CAPTURE_MS || this.totalRecordedBytes >= MAX_CAPTURE_BYTES) throw errorFor('sizeLimit');
    const mimeType = MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type));
    if (!mimeType) throw errorFor('unsupported');
    const recorder = new MediaRecorder(this.stream, { mimeType, audioBitsPerSecond: 32000 });
    const parts = [];
    let size = 0;
    this.mediaRecorder = recorder;
    let finish;
    this.chunkFinished = new Promise(resolve => { finish = resolve; });
    recorder.ondataavailable = ({ data }) => {
      if (!data?.size) return;
      parts.push(data);
      size += data.size;
      if (size >= this.maxChunkSize && this.state === 'recording' && !this.rotating) this.rotateChunk();
    };
    recorder.onstop = () => {
      clearTimeout(this.chunkTimeout);
      // The final dataavailable event precedes stop. Include it before persisting
      // or uploading; snapshots taken before stop lose the tail of each chunk.
      if (parts.length) {
        const chunkIndex = this.chunkIndex++;
        const blob = new Blob(parts, { type: recorder.mimeType || mimeType });
        this.totalRecordedBytes += blob.size;
        this.unsavedChunks.set(chunkIndex, { blob, checkpoint: this.checkpoint() });
        if (this.totalRecordedBytes >= MAX_CAPTURE_BYTES) {
          if (this.state === 'paused') this.notifyError(errorFor('sizeLimit'));
          else this.captureFailed(errorFor('sizeLimit'));
        }
      }
      if (this.mediaRecorder === recorder) this.mediaRecorder = null;
      finish();
      // An unsolicited stop (for example a removed microphone) is a failure,
      // whereas expected stops belong to pause, rotation, or finalization.
      if (!this.closingChunk && !this.disposed) this.captureFailed(errorFor('microphoneLost'));
    };
    recorder.onerror = () => this.captureFailed(errorFor('microphoneLost'));
    let didStart = false;
    let timeout;
    try {
      await new Promise((resolve, reject) => {
        timeout = setTimeout(() => reject(errorFor('microphoneError')), 10000);
        recorder.onstart = () => {
          if (this.disposed || !this.isStreamRecordable() || !['starting', 'recording'].includes(this.state)) {
            return reject(errorFor('microphoneLost'));
          }
          this.setState('recording');
          this.startTimer();
          resolve();
        };
        try { recorder.start(1000); didStart = true; } catch (_) { reject(errorFor('microphoneError')); }
      });
    } catch (error) {
      // A synchronous start failure emits no stop event. Settle its promise
      // explicitly; a later Stop must never wait for an event that cannot fire.
      recorder.onstart = null;
      if (!didStart) {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        if (this.mediaRecorder === recorder) this.mediaRecorder = null;
        finish();
      } else {
        await this.finishChunk();
      }
      throw error;
    } finally {
      clearTimeout(timeout);
    }
    this.chunkTimeout = setTimeout(() => this.rotateChunk(), this.maxChunkDuration);
  }

  async finishChunk() {
    clearTimeout(this.chunkTimeout);
    const recorder = this.mediaRecorder;
    if (!recorder) return;
    this.closingChunk = true;
    try {
      if (recorder.state !== 'inactive') recorder.stop();
      await this.chunkFinished;
    } finally {
      this.closingChunk = false;
    }
  }

  async rotateChunk() {
    if (this.state !== 'recording' || this.rotating || this.disposed) return;
    this.rotating = this.serialize(async () => {
      if (this.state !== 'recording') return;
      this.setState('starting');
      this.stopTimer();
      await this.finishChunk();
      if (this.state !== 'starting') return;
      await this.startChunk();
      // Storage can span several rotations. Keep capture and commands moving;
      // persistChunks serializes commits and Stop still awaits all saved audio.
      this.persistChunks().then(() => {
        this.sendPendingChunks().catch(() => {});
      }, error => this.captureFailed(this.publicError(error, 'storageError')));
    }).catch(error => this.captureFailed(this.publicError(error, 'storageError')))
      .finally(() => { this.rotating = null; });
    await this.rotating;
  }

  captureFailed(error) {
    if (this.disposed || !['recording', 'starting'].includes(this.state)) return;
    this.stopTimer();
    this.setState('error');
    this.notifyError(error);
    this.serialize(async () => {
      await this.finishChunk();
      await this.persistChunks();
      this.sendPendingChunks().catch(() => {});
    }).catch(cause => this.notifyError(this.publicError(cause, 'storageError')));
  }

  persistChunks() {
    if (this.persistTask) return this.persistTask;
    const task = (async () => {
      for (const [chunkIndex, chunk] of this.unsavedChunks) {
        try {
          await this.outbox.saveChunk(this.sessionId, chunkIndex, chunk.blob, chunk.checkpoint);
          this.unsavedChunks.delete(chunkIndex);
        } catch (_) {
          throw errorFor('storageError');
        }
      }
    })();
    this.persistTask = task;
    task.finally(() => { if (this.persistTask === task) this.persistTask = null; }).catch(() => {});
    return task;
  }

  pauseRecording() {
    return this.serialize(async () => {
      if (this.state !== 'recording') return;
      this.setState('paused');
      this.stopTimer();
      try {
        await this.finishChunk();
        await this.persistChunks();
        this.sendPendingChunks().catch(() => {});
      } catch (cause) {
        const error = this.publicError(cause, 'storageError');
        this.setState('error');
        this.notifyError(error);
        throw error;
      }
    });
  }

  resumeRecording() {
    return this.serialize(async () => {
      if (!this.canResume) return;
      this.setState('starting');
      try {
        if (Date.now() - this.createdAt >= 12 * 60 * 60 * 1000) throw errorFor('sessionError');
        await this.persistChunks();
        await this.acquireStream();
        await this.startChunk();
        this.sendPendingChunks().catch(() => {});
      } catch (cause) {
        this.stopTimer();
        const error = this.publicError(cause, 'sessionError');
        this.setState('error');
        this.notifyError(error);
        throw error;
      }
    });
  }

  sendPendingChunks() {
    if (this.streamTask) return this.streamTask;
    const task = (async () => {
      await this.persistChunks();
      if (this.transport?.mode !== 'websocket') return;
      while (this.nextChunkToSend < this.chunkIndex) {
        if (this.unsavedChunks.has(this.nextChunkToSend)) await this.persistChunks();
        const chunk = await this.outbox.getChunk(this.sessionId, this.nextChunkToSend);
        if (!chunk) throw errorFor('storageError');
        if (!await this.transport.sendChunk(chunk.chunkIndex, chunk.blob)) return;
        this.nextChunkToSend++;
        // Keep the disk copy of EVERY chunk for a complete fallback upload.
      }
    })();
    this.streamTask = task;
    task.catch(cause => this.notifyError(this.publicError(cause, 'uploadError')));
    task.finally(() => { if (this.streamTask === task) this.streamTask = null; }).catch(() => {});
    return task;
  }

  async completeLocally(result, setLatestNote, addLoadingNote) {
    const sessionId = this.sessionId;
    await this.outbox.deleteSession(sessionId);
    this.transport?.close();
    this.transport = null;
    this.sessionId = null;
    this.stopData = null;
    this.unsavedChunks.clear();
    this.nextChunkToSend = 0;
    this.chunkIndex = 0;
    this.totalElapsedTime = 0;
    this.totalRecordedBytes = 0;
    this.releaseSessionLock?.();
    this.releaseSessionLock = null;
    this.options.onTick?.(0);
    this.setState('idle');
    if (result) {
      addLoadingNote?.(sessionId);
      setLatestNote?.(result.noteId ? { id: result.noteId } : null, sessionId);
    } else {
      this.notifyError(errorFor('noAudio'));
    }
  }

  async submitStop(setLatestNote, addLoadingNote) {
    // Audio and its checkpoint commit atomically BEFORE the final metadata.
    await this.persistChunks();
    if (!this.chunkIndex) {
      // No note/usage is claimed. The existing server cleanup abandons empty
      // sessions; there is no missing chunk zero to wait for.
      await this.completeLocally(null, setLatestNote, addLoadingNote);
      return;
    }
    await this.outbox.saveSession({ ...this.checkpoint(), ...this.stopData });
    await this.sendPendingChunks();
    if (this.transport?.mode !== 'websocket' || this.nextChunkToSend < this.chunkIndex) {
      this.transport = this.transport || this.createTransport();
      const chunks = await this.outbox.listChunks(this.sessionId);
      if (chunks.length !== this.chunkIndex) throw errorFor('storageError');
      await this.transport.uploadRecording(this.stopData.actualDurationSeconds, chunks);
    }
    let result;
    try {
      result = await this.request('/api/recording/stop', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.stopData),
      });
    } catch (_) {
      throw errorFor('stopError');
    }
    await this.completeLocally(result, setLatestNote, addLoadingNote);
  }

  stop(setLatestNote, addLoadingNote) {
    return this.serialize(async () => {
      if (!this.sessionId) return;
      const retryingStop = Boolean(this.stopData);
      this.setState('stopping');
      this.stopTimer();
      try {
        if (retryingStop) {
          const status = await this.sessionStatus(this.sessionId);
          if (status.overallStatus !== 'recording' && status.finalChunkIndex === this.stopData.finalChunkIndex &&
              status.actualDurationSeconds === this.stopData.actualDurationSeconds) {
            await this.completeLocally({ noteId: status.noteId }, setLatestNote, addLoadingNote);
            return;
          }
        }
        await this.finishChunk();
        this.releaseStream();
        this.stopData = this.stopData || {
          sessionId: this.sessionId, finalChunkIndex: Math.max(0, this.chunkIndex - 1),
          actualDurationSeconds: Math.ceil(this.totalElapsedTime / 1000),
        };
        await this.submitStop(setLatestNote, addLoadingNote);
      } catch (cause) {
        this.releaseStream();
        const error = this.publicError(cause, 'storageError');
        this.setState('error');
        this.notifyError(error);
        throw error;
      }
    });
  }

  async findRecoverableSessions() {
    await this.outbox.pruneExpired();
    const sessions = await this.outbox.listSessions();
    const locks = navigator.locks ? await navigator.locks.query() : { held: [] };
    const active = new Set(locks.held.map(lock => lock.name));
    const owned = [];
    for (const session of sessions) {
      if (session.sessionId === this.sessionId || active.has(sessionLockName(session.sessionId))) continue;
      try {
        // Browser storage is shared across sign-ins. Never expose another user's
        // recording or transmit its bytes without server ownership verification.
        await this.sessionStatus(session.sessionId);
        owned.push(session);
      } catch (error) {
        if (![401, 404].includes(error.status)) throw errorFor('sessionError');
      }
    }
    return owned;
  }

  recoverSession(sessionId, setLatestNote, addLoadingNote) {
    return this.serialize(async () => {
      if (this.sessionId) throw errorFor('sessionError');
      const sessions = await this.outbox.listSessions();
      const session = sessions.find(item => item.sessionId === sessionId);
      if (!session) throw errorFor('sessionError');
      const status = await this.sessionStatus(sessionId);
      await this.lockSession(sessionId);
      this.sessionId = sessionId;
      this.createdAt = session.createdAt;
      this.chunkIndex = session.nextChunkIndex || 0;
      this.totalElapsedTime = (session.actualDurationSeconds || 0) * 1000;
      this.stopData = {
        sessionId, finalChunkIndex: session.finalChunkIndex ?? Math.max(0, this.chunkIndex - 1),
        actualDurationSeconds: session.actualDurationSeconds || 0,
      };
      this.setState('stopping');
      try {
        if (status.overallStatus !== 'recording' && status.finalChunkIndex === this.stopData.finalChunkIndex &&
            status.actualDurationSeconds === this.stopData.actualDurationSeconds) {
          // A lost stop response or the legacy backstop may have completed the
          // session already. Its canonical outcome takes precedence over retry.
          await this.completeLocally({ noteId: status.noteId }, setLatestNote, addLoadingNote);
          return;
        }
        this.transport = this.createTransport();
        this.transport.mode = 'fallback';
        await this.submitStop(setLatestNote, addLoadingNote);
      } catch (cause) {
        const error = this.publicError(cause, 'stopError');
        this.setState('error');
        this.notifyError(error);
        throw error;
      }
    });
  }

  dispose() {
    this.disposed = true;
    this.transport?.close();
    this.stopTimer();
    clearTimeout(this.chunkTimeout);
    // Preserve the last asynchronously flushed bytes on unmount. A page crash
    // can still lose the current chunk; previously committed chunks survive.
    this.finishChunk().then(() => this.persistChunks()).catch(() => {}).finally(() => {
      this.releaseSessionLock?.();
      this.releaseSessionLock = null;
    });
    this.releaseStream();
  }
}
